import { type Folder, PageWidgetType } from '@efdigital/ll-addon-data';
import { type File } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import {
  type PageWidgetFilesAndFolders,
  type PageWidgetFilesAndFoldersLike,
} from '@efdigital/ll-addon-data/types/content/objects/page-widget-files-and-folders';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { DragToSort } from '@efdigital/ll-admin-v2/components/drag-to-sort.gts';
import { addOne } from '@efdigital/ll-admin-v2/helpers/add-one.ts';
import {
  getFileIcon,
  getFolderIcon,
  parseTypedFileOrFolderId,
} from '@efdigital/ll-admin-v2/lib/file-icon.ts';
import {
  type ReactiveStringArray,
  StringArrayResource,
} from '@efdigital/ll-admin-v2/resources/string-array';
import { uniqueId } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import FilePicker, {
  type FilePickerResult,
} from '../../pickers/file-picker.gts';
import type {
  PageWidgetComponentSignature,
  WidgetEditor,
} from '../page-widget-signature.ts';

type FilesItemDisplayType = PageWidgetFilesAndFolders['itemDisplay'];
type FilesListDisplayType = PageWidgetFilesAndFolders['listDisplay'];

const FILES_ITEM_DISPLAY_TYPES: Record<FilesItemDisplayType, string> = {
  'link-simple': 'Link (text only)',
  'link-abstract': 'Link (with icon, abstract)',
  embed: 'Embed',
} as const;

const FILES_ITEM_DISPLAY_TYPES_ARRAY = Object.entries(
  FILES_ITEM_DISPLAY_TYPES
).map(([key, value]) => ({
  key,
  value,
}));

const FILES_LIST_DISPLAY_TYPES: Record<FilesListDisplayType, string> = {
  list: 'List',
} as const;

const FILES_LIST_DISPLAY_TYPES_ARRAY = Object.entries(
  FILES_LIST_DISPLAY_TYPES
).map(([key, value]) => ({
  key,
  value,
}));

function formatFileOrFolder(
  fileOrFolder: FilePickerResult
): FilePickerResult & {
  leafname: string;
  icon: string;
} {
  const leafname = fileOrFolder.item.id!.substring(
    fileOrFolder.item.id!.lastIndexOf('|') + 1
  );
  let icon = getFolderIcon();
  if (fileOrFolder.type === DataTypeContent.File) {
    const file = fileOrFolder.item as File;
    icon = getFileIcon(file.contentType);
  }

  return {
    ...fileOrFolder,
    leafname,
    icon,
  };
}

export class PageWidgetFilesAndFoldersComponent extends Component<PageWidgetComponentSignature> {
  @service declare store: Store;

  get widget() {
    return this.args.widget as PageWidgetFilesAndFolders;
  }

  setItemDisplay = (event: Event) => {
    const itemDisplay = (event.target as HTMLSelectElement)
      .value as PageWidgetFilesAndFolders['itemDisplay'];
    this.widget.itemDisplay = itemDisplay;
  };

  setListDisplay = (event: Event) => {
    const listDisplay = (event.target as HTMLSelectElement)
      .value as PageWidgetFilesAndFolders['listDisplay'];
    this.widget.listDisplay = listDisplay;
  };

  updateFileAndFolderIds = (newValues: string[]) => {
    this.widget.fileAndFolderIds = newValues;
  };

  hasMultipleItems = (fileAndFolderIds: string[]) => {
    return fileAndFolderIds.length > 1;
  };

  itemSelectedAtIndex = (
    fileAndFolderIds: ReactiveStringArray,
    index: number,
    result: FilePickerResult
  ) => {
    if (result.type === DataTypeContent.File) {
      fileAndFolderIds.setValueAt(
        index,
        `${DataTypeContent.File}:${result.item.id!}`
      );
      void this.args.pageManager.addFiles([result.item as File]);
    } else if (result.type === DataTypeContent.Folder) {
      fileAndFolderIds.setValueAt(
        index,
        `${DataTypeContent.Folder}:${result.item.id!}`
      );
      void this.args.pageManager.addFolders([result.item as Folder]);
    }
  };

  lookupItem: (fileOrFolderId: string) => Promise<FilePickerResult> = async (
    fileOrFolderId: string
  ) => {
    const { type, id } = parseTypedFileOrFolderId(fileOrFolderId);

    const items =
      type === DataTypeContent.File
        ? await this.args.pageManager.findFiles([id])
        : await this.args.pageManager.findFolders([id]);

    if (items.length === 1) {
      return {
        type,
        item: items[0]!,
      };
    }

    throw new Error(`Item not found`);
  };

  get navigationFolders() {
    const { clientId, cohortId } = this.args.pageManager.context;
    return [
      {
        id: `|${clientId}|Cohorts|${cohortId}`,
        label: 'Cohort',
        help: 'Files for this cohort',
      },
      {
        id: `|${clientId}|Client Programs`,
        label: 'Client Programs',
        help: "Files for this client's programs",
      },
      { id: `|global`, label: 'Global Files', help: 'Files for all clients' },
    ];
  }

  get startingFolderId() {
    const { clientId, cohortId } = this.args.pageManager.context;
    const folderId = `|${clientId}|Cohorts|${cohortId}`;
    return `${DataTypeContent.Folder}:${folderId}`;
  }

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      {{#let
        (StringArrayResource
          this.widget.fileAndFolderIds this.updateFileAndFolderIds
        )
        as |fileAndFolderIds|
      }}
        <div class="card-body">
          <div class="form-group mb-4">
            <label for="{{uniqueId}}-fileIds">Files</label>
            <div class="list-group mb-3 gap-1">
              <DragToSort
                @items={{fileAndFolderIds.values}}
                @updateItems={{this.updateFileAndFolderIds}}
              >
                <:item as |dropZone draggable fileOrFolderId index|>
                  <div class="input-group" {{dropZone index}}>
                    <span
                      class="input-group-text justify-content-center fw-bold drag-handle w-3rem"
                      {{draggable fileOrFolderId index}}
                      title="Drag to reorder"
                    >
                      {{addOne index}}
                    </span>
                    <FilePicker
                      @canSelectFolders={{true}}
                      @canSelectFiles={{true}}
                      @navigationFolders={{this.navigationFolders}}
                      @onSelect={{fn
                        this.itemSelectedAtIndex
                        fileAndFolderIds
                        index
                      }}
                    >
                      <:input as |showModal|>
                        {{#if fileOrFolderId}}
                          <Await @promise={{this.lookupItem fileOrFolderId}}>
                            <:success as |result|>
                              {{#let
                                (formatFileOrFolder result)
                                as |fileOrFolder|
                              }}
                                <button
                                  aria-label="Select file or folder"
                                  class="btn btn-outline-primary rounded-0 fs-4 px-3 py-0"
                                  type="button"
                                  {{on "click" (fn showModal fileOrFolderId)}}
                                >
                                  <iconify-icon
                                    inline
                                    icon={{fileOrFolder.icon}}
                                  />
                                </button>
                                <input
                                  type="text"
                                  readonly
                                  value={{fileOrFolder.leafname}}
                                  class="form-control list-group-item text-truncate"
                                  aria-label="File name"
                                />
                              {{/let}}
                            </:success>
                            <:error as |error|>
                              {{error.message}}
                            </:error>
                          </Await>
                        {{else}}
                          <button
                            aria-label="Select file or folder"
                            class="btn btn-outline-primary rounded-0 fs-4 px-3 py-0"
                            type="button"
                            {{on "click" (fn showModal this.startingFolderId)}}
                          >
                            <iconify-icon inline icon="ph:magnifying-glass" />
                          </button>

                          <input
                            type="text"
                            readonly
                            value="Select a file or folder..."
                            class="form-control list-group-item text-truncate"
                            aria-label="File or folder name"
                          />
                        {{/if}}
                      </:input>
                    </FilePicker>

                    <button
                      aria-label="Remove folder"
                      class="btn btn-outline-primary rounded-end-1"
                      type="button"
                      {{on "click" (fn fileAndFolderIds.removeValueAt index)}}
                    ><iconify-icon inline icon="ph:x-bold" /></button>
                  </div>
                </:item>
              </DragToSort>
            </div>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              {{on "click" (fn fileAndFolderIds.addValue "")}}
            >
              <iconify-icon inline icon="ph:plus-bold" />
              File / Folder
            </button>
          </div>

          <div class="form-group mb-4">
            <label for="{{uniqueId}}-itemDisplay">Display each file as</label>
            <select
              class="form-control"
              id="{{uniqueId}}-itemDisplay"
              {{selectValue this.widget.itemDisplay}}
              {{on "change" this.setItemDisplay}}
            >
              {{#each FILES_ITEM_DISPLAY_TYPES_ARRAY as |itemDisplay|}}
                <option value={{itemDisplay.key}}>{{itemDisplay.value}}</option>
              {{/each}}
            </select>
          </div>

          {{#if (this.hasMultipleItems fileAndFolderIds.values)}}
            <div class="form-group mb-4">
              <label for="{{uniqueId}}-listDisplay">Group multiple items into</label>
              <select
                class="form-control"
                id="{{uniqueId}}-listDisplay"
                {{selectValue this.widget.listDisplay}}
                {{on "change" this.setListDisplay}}
              >
                {{#each FILES_LIST_DISPLAY_TYPES_ARRAY as |listDisplay|}}
                  <option
                    value={{listDisplay.key}}
                  >{{listDisplay.value}}</option>
                {{/each}}
              </select>
            </div>
          {{/if}}
        </div>
      {{/let}}
    {{/let}}
  </template>
}

const DEFAULT_WIDGET_FILES_AND_FOLDERS: Omit<
  PageWidgetFilesAndFoldersLike,
  'wid'
> = {
  widgetType: PageWidgetType.FilesAndFolders,
  title: 'Files and Folders',
  css: '',
  fileAndFolderIds: [],
  itemDisplay: 'link-simple',
  listDisplay: 'list',
  linkCss: 'btn btn-primary',
};

export const FilesAndFoldersWidgetEditor: WidgetEditor = {
  icon: 'ph:file-bold',
  label: 'Files and Folders',
  component: PageWidgetFilesAndFoldersComponent,
  dataType: DataTypeContent.PageWidgetFilesAndFolders,
  widgetData: DEFAULT_WIDGET_FILES_AND_FOLDERS,
};
